import { useState } from 'react';
import { Mic, Eye, EyeOff, ArrowRight, Loader2, User, Lock, Mail } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function AuthPage({ onLogin }) {
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = mode === 'login' ? '/auth/login' : '/auth/register';
      const payload = mode === 'login' ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(endpoint, payload);
      
      if (data.access_token) {
        localStorage.setItem('vocala_token', data.access_token);
        if (data.workspace_id) {
          localStorage.setItem('vocala_workspace_id', data.workspace_id);
        }
        // Flag for new user onboarding
        if (mode === 'register') {
          localStorage.setItem('vocala_show_onboarding', 'true');
        }
      }
      toast.success(mode === 'login' ? 'Welcome back!' : '🎉 Welcome to Vocala! Your workspace is ready.');
      onLogin(data.user || data);
    } catch (err) {
      const msg = err.response?.data?.detail || (mode === 'login' ? 'Invalid credentials' : 'Registration failed');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#1e40af] via-blue-600 to-emerald-500 flex flex-col justify-center py-12 sm:px-6 lg:px-8"
      data-testid="auth-page"
      style={{ fontFamily: 'Georgia, serif' }}
    >
      {/* Logo — above card */}
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center mb-2">
        <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center shadow-2xl mx-auto mb-4">
          <Mic className="w-6 h-6 text-blue-600" />
        </div>
        <h1 className="text-3xl font-black text-white tracking-tight">
          Vocala<span className="text-emerald-300">.</span>
        </h1>
        <p className="text-blue-100 text-sm mt-2">
          {mode === 'login' ? 'Sign in to your workspace' : '✨ Create your account & get your AI recruitment workspace'}
        </p>
      </div>

      {/* Card */}
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white/95 backdrop-blur-xl py-8 px-4 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] sm:rounded-3xl sm:px-10 border border-white/20">

          {/* Mode toggle */}
          <div className="flex bg-slate-100/80 rounded-xl p-1 mb-6">
            <button
              data-testid="login-tab"
              onClick={() => setMode('login')}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                mode === 'login' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Sign In
            </button>
            <button
              data-testid="register-tab"
              onClick={() => setMode('register')}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                mode === 'register' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Create Account
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <label className="text-slate-600 text-sm font-medium block mb-1.5">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    data-testid="name-input"
                    type="text"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    required
                    className="appearance-none block w-full pl-10 pr-4 py-3.5 border border-slate-200/70 rounded-xl bg-slate-50/50 shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-slate-800 transition-all hover:bg-white"
                    placeholder="Jane Smith"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="text-slate-600 text-sm font-medium block mb-1.5">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  data-testid="email-input"
                  type="email"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  required
                  className="appearance-none block w-full pl-10 pr-4 py-3.5 border border-slate-200/70 rounded-xl bg-slate-50/50 shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-slate-800 transition-all hover:bg-white"
                  placeholder="you@company.com"
                />
              </div>
            </div>

            <div>
              <label className="text-slate-600 text-sm font-medium block mb-1.5">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  data-testid="password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  required
                  minLength={6}
                  className="appearance-none block w-full pl-10 pr-11 py-3.5 border border-slate-200/70 rounded-xl bg-slate-50/50 shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-slate-800 transition-all hover:bg-white"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              data-testid="submit-auth-btn"
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3.5 px-4 border border-transparent rounded-xl shadow-lg text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-emerald-600 hover:shadow-xl hover:scale-[1.01] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all active:scale-[0.98] mt-2 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>{mode === 'login' ? 'Sign In' : 'Create Account'} <ArrowRight className="w-4 h-4" /></>
              )}
            </button>

            {mode === 'register' && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-xl">
                <p className="text-xs text-slate-600 leading-relaxed">
                  <strong className="text-blue-600">What you'll get:</strong> Your own workspace as Owner, ability to invite team members, and full access to AI sourcing, voice screening & scheduling.
                </p>
              </div>
            )}
          </form>

          {mode === 'login' && (
            <p className="text-center text-sm text-slate-500 mt-4">
              Test account: <span className="text-blue-600 font-medium">test@vocala.com</span> / test123456
            </p>
          )}
        </div>

        <p className="text-center text-xs text-white/50 mt-6">
          © {new Date().getFullYear()} Vocala · A DS Technologies Product
        </p>
      </div>
    </div>
  );
}
